import { ShadowDiffPanel } from '@/client/components/timesheets/ShadowDiffPanel';
import { ShadowEditPanel } from '@/client/components/timesheets/ShadowEditPanel';
import { ShadowSidebar } from '@/client/components/timesheets/ShadowSidebar';
import { useAdminGuard } from '@/client/hooks/useAdminGuard';
import { getMonthRange, getWeekRange } from '@/client/lib/dates';
import { trpc } from '@/client/utils/trpc';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_app/timesheets/shadow')({
  component: ShadowTimesheetPage,
});

function ShadowTimesheetPage() {
  const { me, isLoading: meLoading } = useAdminGuard();

  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [view, setView] = useState<'week' | 'month'>('week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [activeTab, setActiveTab] = useState<'edit' | 'diff'>('edit');

  const weekRange = getWeekRange(weekOffset);
  const monthRange = getMonthRange(monthOffset);
  const range = view === 'week' ? weekRange : monthRange;

  const { data: existingPeriods = [] } = trpc.shadowTimesheets.listByUserProject.useQuery(
    { user_id: selectedUserId, project_id: selectedProjectId },
    { enabled: !!selectedUserId && !!selectedProjectId },
  );

  function handleJumpToPeriod(period_from: string, period_to: string) {
    const days =
      Math.round(
        (new Date(period_to).getTime() - new Date(period_from).getTime()) / (24 * 60 * 60 * 1000),
      ) + 1;
    if (days === 7) {
      const targetMonday = new Date(`${period_from}T00:00:00`);
      const now = new Date();
      const dow = now.getDay();
      const currentMonday = new Date(now);
      currentMonday.setDate(now.getDate() + (dow === 0 ? -6 : 1 - dow));
      const offset = Math.round(
        (targetMonday.getTime() - currentMonday.getTime()) / (7 * 24 * 60 * 60 * 1000),
      );
      setWeekOffset(offset);
      setView('week');
    } else {
      const [y, m] = period_from.split('-').map(Number);
      const now = new Date();
      const offset = (y - now.getFullYear()) * 12 + (m - now.getMonth() - 1);
      setMonthOffset(offset);
      setView('month');
    }
  }

  if (meLoading || me?.role !== 'ADMIN') return null;

  return (
    <div className="-mx-6 -my-6 flex h-full overflow-hidden">
      <ShadowSidebar
        selectedUserId={selectedUserId}
        onUserChange={(id) => { setSelectedUserId(id); }}
        selectedProjectId={selectedProjectId}
        onProjectChange={(id) => { setSelectedProjectId(id); }}
        view={view}
        onViewChange={setView}
        periodLabel={range.label}
        onPrev={() => view === 'week' ? setWeekOffset((o) => o - 1) : setMonthOffset((o) => o - 1)}
        onNext={() => view === 'week' ? setWeekOffset((o) => o + 1) : setMonthOffset((o) => o + 1)}
        onToday={() => view === 'week' ? setWeekOffset(0) : setMonthOffset(0)}
        showToday={view === 'week' ? weekOffset !== 0 : monthOffset !== 0}
        existingPeriods={existingPeriods}
        currentPeriodFrom={range.from}
        onJumpToPeriod={handleJumpToPeriod}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Tab bar */}
        <div className="flex items-center gap-1 border-b px-6 py-2 shrink-0">
          {(['edit', 'diff'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'edit' ? 'Edit' : 'Diff'}
            </button>
          ))}
        </div>

        {/* Panel */}
        <div className="flex flex-1 overflow-hidden">
          {activeTab === 'edit' ? (
            <ShadowEditPanel
              key={`${selectedUserId}-${selectedProjectId}-${range.from}-${range.to}`}
              userId={selectedUserId}
              projectId={selectedProjectId}
              periodFrom={range.from}
              periodTo={range.to}
              onUserReassigned={(id) => { setSelectedUserId(id); }}
            />
          ) : (
            <ShadowDiffPanel
              key={`${selectedUserId}-${selectedProjectId}-${range.from}-${range.to}`}
              userId={selectedUserId}
              projectId={selectedProjectId}
              periodFrom={range.from}
              periodTo={range.to}
            />
          )}
        </div>
      </div>
    </div>
  );
}
